import ListItem from 'components/Luxkit/List/ListItem'
import ListItemPrice from 'components/Luxkit/List/ListItemPrice'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import React, { useCallback, useMemo } from 'react'
import { getCheapestRoomOptions, OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'
import { takeCurrentOfferState } from 'contexts/OfferPage/offerPageStateReducer'
import RadioButton from 'components/Luxkit/RadioButton/RadioButton'
import { getOptimizelyExperimentVariation, OptimizelyExperiments } from 'lib/optimizely/optimizelyUtils'
import { getPackageViewPricingPerNight } from 'lib/offer/getPackageViewPricingPerNightUtils'

function getOptionPriceLabel(option: OfferPackageOptionView, isFromPrice: boolean) {
  if (!option.hotelAvailable) {
    return 'SOLD OUT'
  } else if (isFromPrice) {
    return 'From'
  }
  return ''
}

interface Props {
  room?: App.PackageRoomType;
  withFlights?: boolean;
  isFromPrice: boolean;
  packageOptions: Array<OfferPackageOptionView>;
  onSelect?: () => void;
  offer: App.Offer;
  bundleOfferId?: string;
  currentOfferId?: string;
  displayMemberPrice: boolean;
  isPerNightPricingTestEnabled: boolean;
}

function OfferCalendarRoomOptions(props: Props) {
  const { room, isFromPrice, withFlights, onSelect, offer, currentOfferId, bundleOfferId, displayMemberPrice, isPerNightPricingTestEnabled } = props
  const dispatch = useOfferPageDispatch()

  const packageOptions = useMemo(() => {
    if (isPerNightPricingTestEnabled) {
      return props.packageOptions.map((packageOption) => {
        return getPackageViewPricingPerNight(packageOption, packageOption.package.duration)
      })
    } else {
      return props.packageOptions
    }
  }, [isPerNightPricingTestEnabled, props.packageOptions])

  const onItemClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    const id = e.currentTarget.dataset.roomid
    const option = packageOptions.find(opt => opt.package.roomType?.id === id)
    dispatch({ type: OfferPageActions.setRoomType, room: option?.package.roomType, offerId: currentOfferId })
    onSelect?.()
  }, [dispatch, onSelect, packageOptions, currentOfferId])

  const getPrice = (option: OfferPackageOptionView) => {
    if (offer.bundledWithFlightsOnly && !option.flightPrice) {
      return undefined
    }

    return option.hotelAvailable ? (
      Math.ceil(((displayMemberPrice ? option.hotelMemberPrice : option.hotelPrice) + (option.propertyFees ?? 0)) + (withFlights ? option.flightPrice : 0))
    ) : undefined
  }

  return (<>
    <ListItem
      title="View availability for all rooms"
      selected={!room}
      onClick={onItemClick}
      startIcon={<RadioButton name="room" checked={!room} />}
    />
    {packageOptions.map(option => option.package.roomType && <ListItem
      key={option.package.roomType.id}
      title={option.package.roomType.name}
      disabled={!option.hotelAvailable}
      data-roomid={option.package.roomType.id}
      selected={room?.id === option.package.roomType.id}
      onClick={onItemClick}
      startIcon={<RadioButton name="room" checked={room?.id === option.package.roomType.id} />}
    >
      <ListItemPrice price={!bundleOfferId ? getPrice(option) : undefined} label={getOptionPriceLabel(option, isFromPrice)} />
    </ListItem>)}
  </>)
}

export default withOfferPageAndReduxState((currentState, reduxState) => {
  const state = takeCurrentOfferState(currentState)

  return {
    room: state.room,
    bundleOfferId: state.bundleOffer?.id,
    currentOfferId: state.paginator?.currentOfferId,
    packageOptions: getCheapestRoomOptions(state, reduxState),
    withFlights: state.flights,
    isFromPrice: !state.checkInDate,
    offer: state.offer,
    displayMemberPrice: state.displayMemberPrice,
    isPerNightPricingTestEnabled: !!getOptimizelyExperimentVariation(reduxState, OptimizelyExperiments.pricePerNight),
  }
})(OfferCalendarRoomOptions)
